import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetEmployeeAdvanceLedgerQuery } from "../../../store/appApi";
import {
  formatCurrency,
  formatDateLabel,
  formatRoleLabel,
  toDateInputValue,
} from "./advancesConfig";
import { deleteAdvanceApi, updateAdvanceApi } from "../../../services/advances";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const typeChip = (type) =>
  type === "Deduction"
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

const balanceClass = (value) => {
  if (value > 0) return "text-rose-600";
  if (value < 0) return "text-emerald-600";
  return "text-gray-500";
};

const AdvanceDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { employeeId } = useParams();

  const { data, isLoading, isFetching } = useGetEmployeeAdvanceLedgerQuery(
    employeeId,
    { skip: !employeeId },
  );

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);

  const employee = data?.employee || null;
  const ledger = Array.isArray(data?.ledger) ? data.ledger : [];
  const summary = data?.summary || {};
  const loading = isLoading || isFetching;

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setEditDraft({
      type: entry.type,
      date: toDateInputValue(entry.date),
      amount: entry.amount,
      remarks: entry.remarks || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const updateDraft = (key, value) =>
    setEditDraft((prev) => ({ ...prev, [key]: value }));

  const saveEdit = async (entryId) => {
    const numericAmount = Number(editDraft.amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }

    try {
      setRowBusy(entryId);
      await updateAdvanceApi(entryId, {
        type: editDraft.type,
        date: editDraft.date,
        amount: numericAmount,
        remarks: editDraft.remarks,
      });
      dispatch(appApi.util.invalidateTags(["Advances"]));
      Swal.fire({
        icon: "success",
        title: "Updated",
        timer: 1400,
        showConfirmButton: false,
      });
      cancelEdit();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to update",
        text:
          error?.response?.data?.message ||
          "Something went wrong while updating this entry.",
      });
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async (entry) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete this entry?",
      text: `${entry.type} of ${formatCurrency(entry.amount)} on ${formatDateLabel(entry.date)} will be permanently removed.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    try {
      setRowBusy(entry._id);
      await deleteAdvanceApi(entry._id);
      dispatch(appApi.util.invalidateTags(["Advances"]));
      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to delete",
        text:
          error?.response?.data?.message ||
          "Something went wrong while deleting this entry.",
      });
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Advance Detail" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Advance Ledger
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900">
                {loading ? "Loading…" : employee?.name || "Staff member"}
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                {formatRoleLabel(employee?.role)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-full bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                onClick={() => navigate("/advances/add")}
              >
                + Add Entry
              </button>
              <button
                type="button"
                className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700"
                onClick={() => navigate("/advances")}
              >
                Back to list
              </button>
            </div>
          </div>

          <div className="mt-5 grid max-w-xl grid-cols-3 gap-3 text-center text-xs text-gray-600">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="text-gray-400">Total Advance</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {formatCurrency(summary.totalAdvance)}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="text-gray-400">Total Deduction</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {formatCurrency(summary.totalDeduction)}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="text-gray-400">Net Balance</div>
              <div
                className={`mt-1 text-lg font-semibold ${balanceClass(Number(summary.netBalance) || 0)}`}
              >
                {formatCurrency(summary.netBalance)}
              </div>
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Full Ledger
              </h2>
              <p className="text-sm text-gray-500">
                Every advance and deduction, latest first, with a running
                balance.
              </p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
              {summary.entriesCount ?? ledger.length} entries
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Date</th>
                  <th className="px-5 py-2.5 text-left font-medium">Type</th>
                  <th className="px-5 py-2.5 text-right font-medium">Amount</th>
                  <th className="px-5 py-2.5 text-right font-medium">
                    Running Balance
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium">Remarks</th>
                  <th className="px-5 py-2.5 text-left font-medium">
                    Recorded By
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-gray-500">
                      Loading ledger...
                    </td>
                  </tr>
                )}

                {!loading && ledger.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-gray-500">
                      No advances or deductions recorded for this employee yet.
                    </td>
                  </tr>
                )}

                {!loading &&
                  ledger.map((entry) => {
                    const isEditing = editingId === entry._id;
                    const busy = rowBusy === entry._id;

                    if (isEditing) {
                      return (
                        <tr key={entry._id} className="bg-amber-50/40">
                          <td className="px-5 py-2.5">
                            <input
                              type="date"
                              className={inputClass}
                              value={editDraft.date}
                              onChange={(event) =>
                                updateDraft("date", event.target.value)
                              }
                            />
                          </td>
                          <td className="px-5 py-2.5">
                            <select
                              className={inputClass}
                              value={editDraft.type}
                              onChange={(event) =>
                                updateDraft("type", event.target.value)
                              }
                            >
                              <option value="Advance">Advance</option>
                              <option value="Deduction">Deduction</option>
                            </select>
                          </td>
                          <td className="px-5 py-2.5">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className={`${inputClass} text-right`}
                              value={editDraft.amount}
                              onChange={(event) =>
                                updateDraft("amount", event.target.value)
                              }
                            />
                          </td>
                          <td className="px-5 py-2.5 text-right text-gray-400">
                            —
                          </td>
                          <td className="px-5 py-2.5">
                            <input
                              className={inputClass}
                              value={editDraft.remarks}
                              onChange={(event) =>
                                updateDraft("remarks", event.target.value)
                              }
                              placeholder="Remarks"
                            />
                          </td>
                          <td className="px-5 py-2.5 text-gray-600">
                            {entry.createdBy?.name || "—"}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={() => saveEdit(entry._id)}
                              >
                                {busy ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-600"
                                onClick={cancelEdit}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={entry._id}>
                        <td className="px-5 py-2.5 text-gray-600">
                          {formatDateLabel(entry.date)}
                        </td>
                        <td className="px-5 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${typeChip(entry.type)}`}
                          >
                            {entry.type}
                          </span>
                        </td>
                        <td className="px-5 py-2.5 text-right text-gray-900">
                          {formatCurrency(entry.amount)}
                        </td>
                        <td
                          className={`px-5 py-2.5 text-right font-semibold ${balanceClass(Number(entry.runningBalance) || 0)}`}
                        >
                          {formatCurrency(entry.runningBalance)}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {entry.remarks || "—"}
                          {entry.source === "salary-sheet" && (
                            <span className="ml-2 rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700">
                              Salary sheet
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {entry.createdBy?.name || "—"}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <div className="flex justify-end gap-2">
{entry.source === "salary-sheet" ? (
                              <span className="text-xs text-gray-400">Managed by salary sheet</span>
                            ) : (
                              <>
                            <button
                              type="button"
                              disabled={busy}
                              className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => startEdit(entry)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-400 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => handleDelete(entry)}
                            >
                              {busy ? "..." : "Delete"}
                            </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default AdvanceDetail;
